import { useNavigator } from '@repo/toolkit/use-navigator'
import Button from '@repo/ui/button'
import Typography from '@repo/ui/typography'
import { useMutation } from '@tanstack/react-query'
import { useEffect } from 'react'
import { matchPath, useParams, useSearchParams } from 'react-router-dom'

import { authenticateToken } from '../../api/auth/authenticate-token'
import PendingLoader from '../../components/elements/loading/pending-loader'
import {
  getLoginPage,
  getSafeRedirectPath,
  getUserDefaultRedirectionPath
} from '../../components/pages/login/utils'
import { Paths } from '../../lib/constants/path'
import { useSelectedEnrollment } from '../../lib/contexts/selected-enrollment'
import useAuth from '../../lib/hooks/use-auth'
import useTranslate from '../../lib/hooks/use-translate'
import { extractErrorMessage } from '../../lib/utils/functions/misc.functions'

export const AuthenticateTokenPage = () => {
  const { token } = useParams<{ token: string }>()
  const { setUser } = useAuth()
  const { navigate } = useNavigator()
  const { selectedEnrollmentId, setSelectedEnrollmentId } = useSelectedEnrollment()
  const { t } = useTranslate()
  const [searchParams] = useSearchParams()

  // magic links carry the page the user should land on, which also tells their login page
  const redirectPath = getSafeRedirectPath(searchParams.get('redirect'))

  const { mutate, error } = useMutation({
    mutationFn: authenticateToken,
    onSuccess: async (data) => {
      setUser(data)
      // deep links skip the enrollment picker, so select the enrollment the link points to
      if (redirectPath) {
        const enrollmentId = Number(matchPath(Paths.CLIENT_SPACE_LAYOUT, redirectPath)?.params.id)
        if (Number.isInteger(enrollmentId)) await setSelectedEnrollmentId(enrollmentId)
      }
      navigate(redirectPath ?? getUserDefaultRedirectionPath(data, selectedEnrollmentId))
    }
  })

  useEffect(() => {
    document.querySelector('html')?.setAttribute('data-theme', 'admin')
  }, [])

  useEffect(() => {
    if (token) mutate({ token })
  }, [token])

  return error ? (
    <div className="flex flex-col items-center justify-center w-full h-screen gap-4">
      <Typography variant="h1" className="text-red">
        {extractErrorMessage(error)}
      </Typography>
      <Typography variant="body">{t('authenticate.token.error.hint')}</Typography>
      <Button
        label={t('authenticate.token.error.cta')}
        onClick={() => navigate(getLoginPage(redirectPath))}
      />
    </div>
  ) : (
    <PendingLoader text="Authentification en cours..." />
  )
}

export default AuthenticateTokenPage
